import { type CSSProperties, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { ApiKeyInfo, SettingsResponse } from "../../shared/types";
import { DEFAULT_IDLE_MINUTES } from "../../shared/tags";
import { Button, IconButton } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { Sheet } from "../components/ui/Sheet";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { ApiError, api } from "../lib/api";
import { formatAt } from "../lib/zones";
import { OpensChoice, OwnDomainCard } from "../setup/AddressStep";
import { CopyValue, useCopy } from "../setup/parts";
import { looksLikeEmail } from "../setup/rules";
import { type TrackPart, trackParts } from "../setup/trackParts";
import { TRACK_LOGOS } from "../setup/TrackingStep";
import { flipTrack } from "../setup/useTracking";
import type { Run } from "../setup/useSettingsData";
import { automationsBody, IDLE_MAX, IDLE_MIN, idleMinutesFrom, teamOf } from "./automations";
import { SettingsHead } from "./SettingsView";

// --- Web address ---

/** Settings > Web address: the free address, an address the business owns, and what the bare address opens. */
export function DomainsPane({ data, run }: { data: SettingsResponse; run: Run }) {
  const navigate = useNavigate();
  const copy = useCopy();
  const d = data.domain;
  const ownLive = d.custom_domain_status === "verified" && !!d.custom_domain;
  return (
    <>
      <SettingsHead eyebrow="Web address" title="Web address" />
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>Your free address</h2>
          </div>
        </div>
        <div className="card-b">
          <div className="row g12 wrap addrrow" style={{ padding: "14px 16px", borderRadius: 14, border: "1px solid var(--line)", background: "var(--surface-2)" }}>
            <span className="face" style={{ "--s": "36px", background: "var(--good-soft)", color: "var(--good)" } as CSSProperties}>
              <Icon name="globe" />
            </span>
            <div className="sp" style={{ minWidth: 0 }}>
              <div className="b7">{d.subdomain_host}</div>
              {ownLive ? <div className="small muted">Replaced by {d.custom_domain}</div> : null}
            </div>
            {ownLive ? (
              <Pill>Turned off</Pill>
            ) : (
              <Pill tone="good" dot>
                Active
              </Pill>
            )}
            <Button size="s" icon="copy" onClick={() => copy(d.subdomain_host)}>
              Copy
            </Button>
            <Button size="s" icon="pencil" onClick={() => navigate("/setup/address")}>
              Edit
            </Button>
          </div>
        </div>
      </section>
      <OwnDomainCard data={data} run={run} freeHost={d.subdomain_host} settings />
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>Your address opens</h2>
          </div>
        </div>
        <div className="card-b">
          <OpensChoice data={data} run={run} />
        </div>
      </section>
    </>
  );
}

// --- Tracking ---

function TrackRow({ part, run }: { part: TrackPart | { kind: "own"; name: string; on: false; state: "off"; value: null; id?: undefined }; run: Run }) {
  const navigate = useNavigate();
  const toChange = () => navigate("/setup/tracking", { state: { open: part.kind } });
  const pill =
    part.state === "loaded" ? (
      <Pill tone="good" dot>
        Loaded
      </Pill>
    ) : part.state === "bad" ? (
      <Pill tone="warn" dot>
        Needs a valid ID
      </Pill>
    ) : part.state === "waiting" ? (
      <Pill dot>Waiting for a visit</Pill>
    ) : (
      <Pill tone="line">Off</Pill>
    );
  const flip = () => {
    if (!flipTrack(part, run)) toChange();
  };
  return (
    <div className="autorow">
      {TRACK_LOGOS[part.kind]}
      <div className="sp" style={{ minWidth: 0 }}>
        <div className="nm">{part.name}</div>
        {part.value ? (
          <div className="ds">
            <span className="mono">{part.value}</span>
          </div>
        ) : null}
      </div>
      {pill}
      <Button size="s" onClick={toChange}>
        {part.value ? "Change" : "Add"}
      </Button>
      <Toggle on={part.on} onChange={flip} label={`${part.name} on or off`} />
    </div>
  );
}

/** Settings > Tracking: Meta, Google and any pasted script, each on or off, and whether it loads on the pages. */
export function ScriptsPane({ data, run }: { data: SettingsResponse; run: Run }) {
  const parts = trackParts(data);
  const refusal = data.measurement.meta_pixel_id && data.measurement.checks.server.state === "bad" ? data.measurement.checks.server.refusal : null;
  return (
    <>
      <SettingsHead eyebrow="Tracking" title="Tracking" />
      <section className="card ssec">
        <div className="card-b" style={{ paddingTop: 8 }}>
          {parts.map((p) => (
            <TrackRow key={p.id ?? p.kind} part={p} run={run} />
          ))}
          {parts.some((p) => p.kind === "own") ? null : <TrackRow part={{ kind: "own", name: "Your own script", on: false, state: "off", value: null }} run={run} />}
          {refusal ? (
            <div className="note warn">
              <Icon name="info" />
              <div>
                <b>Meta turned an event away.</b> {refusal}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}

// --- Automations ---

/** Settings > Automations: the one clock for stopped watching, and the team who hear about replies. */
export function AutomationsPane({ data, run }: { data: SettingsResponse; run: Run; setData: (d: SettingsResponse) => void }) {
  const toast = useToast();
  const a = data.automations;
  const saved = String(a.idle_minutes ?? DEFAULT_IDLE_MINUTES);
  const [mins, setMins] = useState(saved);
  const [adding, setAdding] = useState("");
  useEffect(() => setMins(saved), [saved]);
  const team = teamOf(data);
  const save = (minutes: number, list: string[], said: string) => run(() => api.settings.saveAutomations(automationsBody(minutes, list)), said);
  const saveMins = () => {
    const n = idleMinutesFrom(mins);
    setMins(String(n));
    if (String(n) !== saved) void save(n, team, "Saved");
  };
  const add = async () => {
    const v = adding.trim();
    if (!looksLikeEmail(v)) return toast({ text: "That doesn’t look like an email address.", icon: "info" });
    if (team.includes(v)) return setAdding("");
    if (await save(Number(saved), [...team, v], `${v} added`)) setAdding("");
  };
  return (
    <>
      <SettingsHead eyebrow="Follow-ups" title="Automations" />
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>Stopped watching after</h2>
            <div className="sub">Then the tag stopped-watching goes on</div>
          </div>
        </div>
        <div className="card-b">
          <div className="row g8">
            <input
              className="inp"
              type="number"
              min={IDLE_MIN}
              max={IDLE_MAX}
              inputMode="numeric"
              value={mins}
              style={{ width: 96 }}
              aria-label="Stopped watching after, in minutes"
              onChange={(e) => setMins(e.target.value)}
              onBlur={saveMins}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveMins();
              }}
            />
            <span className="muted">minutes</span>
          </div>
        </div>
      </section>
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>Tell the team</h2>
            <div className="sub">Hear about replies, messages and answers</div>
          </div>
        </div>
        <div className="card-b">
          <div className="row g8 wrap">
            {team.map((e) => (
              <span key={e} className="tag" style={{ height: 32, padding: "0 6px 0 12px", gap: 6 }}>
                {e}
                <IconButton
                  icon="x"
                  small
                  label={`Remove ${e}`}
                  style={{ width: 22, height: 22 }}
                  onClick={() =>
                    void save(
                      Number(saved),
                      team.filter((x) => x !== e),
                      `${e} removed`,
                    )
                  }
                />
              </span>
            ))}
            <input
              className="inp"
              style={{ width: 240, maxWidth: "100%", height: 34 }}
              type="email"
              inputMode="email"
              placeholder="Add an email, then Enter"
              aria-label="Add an email"
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void add();
              }}
            />
          </div>
        </div>
      </section>
    </>
  );
}

// --- API keys ---

const day = (ts: number | null) => (ts ? formatAt(ts * 1000, { day: "numeric", month: "short" }) : null);

export const masked = (k: string) => `${k.slice(0, 9)}${"•".repeat(15)}${k.slice(-4)}`;

/** The workspace key and the read-only key: what is made, the new key shown once asked for, and making or replacing one. */
export function useApiKeys() {
  const toast = useToast();
  const [work, setWork] = useState<ApiKeyInfo | null>(null);
  const [read, setRead] = useState<ApiKeyInfo | null>(null);
  const [shown, setShown] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    void Promise.all([api.ingest.key("full").catch(() => null), api.ingest.key("read").catch(() => null)]).then(([w, r]) => {
      if (!alive) return;
      setWork(w);
      setRead(r);
    });
    return () => {
      alive = false;
    };
  }, []);

  const issue = async (scope: "full" | "read") => {
    setBusy(true);
    setAsking(false);
    try {
      const res = await api.ingest.rotateKey(scope);
      const info = await api.ingest.key(scope);
      if (scope === "full") {
        setWork(info);
        setShown(true);
        toast({ text: work?.prefix ? "New key made. The old one has stopped working." : "Key made. Copy it now.", icon: "key" });
      } else {
        setRead(info);
        toast({ text: res ? "Read-only key made. Copy it now." : "Done", icon: "key" });
      }
    } catch (e) {
      toast({ text: e instanceof ApiError ? e.message : "We couldn’t make a key just now.", icon: "info" });
    } finally {
      setBusy(false);
    }
  };
  return { work, read, shown, setShown, asking, setAsking, busy, issue };
}

/** The request the "How to send" card copies, with the key in it once it has been shown. */
export const ingestSnippet = (key: string) => `curl https://flashroom.flashsuite.ai/api/ingest \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "someone@example.com",
    "tags": { "add": ["active"], "remove": [] },
    "attributes": { "plan": "advanced" }
  }'`;

/** The "Made 12 Sep. Last used today at 1:12 PM." line under the workspace key. */
export function keyLine(work: ApiKeyInfo): string {
  const used = work.last_used_at ? formatAt(work.last_used_at * 1000, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : null;
  return [day(work.created_at) ? `Made ${day(work.created_at)}.` : null, used ? `Last used ${used}.` : "Not used yet."].filter(Boolean).join(" ");
}

/** Settings > API keys: the workspace key, a read-only key for reporting tools, and how to send. */
export function ApiPane() {
  const { work, read, shown, setShown, asking, setAsking, busy, issue } = useApiKeys();
  const key = work?.key ?? null;
  const snippet = ingestSnippet(shown && key ? key : "frk_live_…");
  const copy = useCopy();
  return (
    <>
      <SettingsHead eyebrow="Developers" title="API keys" />
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>Workspace key</h2>
            <div className="sub">Keep it secret</div>
          </div>
          {work?.prefix ? (
            <Pill tone="good" dot>
              Active
            </Pill>
          ) : (
            <Pill>No key yet</Pill>
          )}
        </div>
        <div className="card-b">
          {!work?.prefix ? (
            <div>
              <Button variant="p" size="s" icon="plus" disabled={busy} onClick={() => void issue("full")}>
                Create key
              </Button>
            </div>
          ) : (
            <>
              <div className="row g8 wrap">
                {key ? (
                  <>
                    <div className="sp" style={{ minWidth: 0 }}>
                      <CopyValue value={key} shown={<span className="fs-norecord">{shown ? key : masked(key)}</span>} title="Copy the key" />
                    </div>
                    <Button size="s" icon="eye" onClick={() => setShown((v) => !v)}>
                      {shown ? "Hide" : "Reveal"}
                    </Button>
                  </>
                ) : (
                  <div className="copyv sp">
                    <span className="muted">{work.prefix}••••••••</span>
                  </div>
                )}
                <Button size="s" icon="refresh" disabled={busy} onClick={() => setAsking(true)}>
                  {key ? "Replace" : "Make a copyable key"}
                </Button>
              </div>
              <div className="hint mt8">
                {keyLine(work)}
              </div>
            </>
          )}
        </div>
      </section>
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>Read-only key</h2>
            <div className="sub">For reporting tools</div>
          </div>
        </div>
        <div className="card-b">
          {read?.key ? (
            <CopyValue value={read.key} shown={<span className="fs-norecord">{read.key}</span>} title="Copy the read-only key" />
          ) : (
            <div>
              <Button variant="p" size="s" icon="plus" disabled={busy} onClick={() => void issue("read")}>
                Create read-only key
              </Button>
            </div>
          )}
        </div>
      </section>
      <section className="card ssec">
        <div className="card-h">
          <div>
            <h2>How to send</h2>
          </div>
          <Button size="s" icon="copy" onClick={() => copy(snippet)}>
            Copy
          </Button>
        </div>
        <div className="card-b">
          <pre className="code">
            {snippet.split(/("[^"]*"|'[^']*')/).map((part, i) => (part.startsWith('"') || part.startsWith("'") ? <span key={i} className="s">{part}</span> : part))}
          </pre>
        </div>
      </section>
      <Sheet
        open={asking}
        onClose={() => setAsking(false)}
        title="Replace the key?"
        footer={
          <>
            <Button onClick={() => setAsking(false)}>Cancel</Button>
            <Button variant="p" disabled={busy} onClick={() => void issue("full")}>
              Replace it
            </Button>
          </>
        }
      >
        <p>Replacing makes a new key. The old one stops working at once, so update your product first.</p>
      </Sheet>
    </>
  );
}

